import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  UserPlusIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addMemberToGroupChat, createGroupChat } from "../api/groupChats.api";
import { ContactRow } from "../api/relationship.type";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import Input from "../components/shared/input";
import { Modal } from "../components/shared/modal/modal";
import ThemedSpinner from "../components/shared/spinner";
import TamaguiInput from "../components/shared/tamagui-input";
import Typography from "../components/shared/typography";
import { useAuth } from "../hooks/AuthContext";
import { useToast } from "../hooks/ToastContext";
import { useLoadContacts } from "../hooks/useLoadContacts";
import { useLoadGroupChats } from "../hooks/useLoadGroupChats";

const GroupsChatPageList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { groupChats, isLoading, isError } = useLoadGroupChats();
  const { contacts, isLoading: contactsLoading } = useLoadContacts();

  const [newGroupChatName, setNewGroupChatName] = useState("");
  const [isAddMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [targetGroupChat, setTargetGroupChat] = useState<{
    id: string;
    name: string | null;
  }>();
  const [contactSearch, setContactSearch] = useState("");

  const filteredContacts = useMemo(() => {
    const query = contactSearch.trim().toLowerCase();
    if (!query) {
      return contacts;
    }

    return contacts.filter((contact) => {
      const first = contact.related_user.first_name?.toLowerCase() || "";
      const last = contact.related_user.last_name?.toLowerCase() || "";
      const email = contact.related_user.email?.toLowerCase() || "";
      const phone = contact.related_user.phone?.toLowerCase() || "";
      return (
        `${first} ${last}`.includes(query) ||
        email.includes(query) ||
        phone.includes(query)
      );
    });
  }, [contactSearch, contacts]);

  const createGroupChatMutation = useMutation({
    mutationFn: createGroupChat,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["group-chats", user?.id] });
      setNewGroupChatName("");
      showToast("Group chat created successfully", "success");
      // Navigate to the new group chat
      navigate(`/group-chat/${data.id}`);
    },
    onError: (error) => {
      showToast("Failed to create group chat", "error");
      console.error("Error creating group chat:", error);
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: ({
      groupChatId,
      userId,
    }: {
      groupChatId: string;
      userId: string;
    }) => addMemberToGroupChat({ groupChatId, userId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["group-chat-members", variables.groupChatId],
      });
      showToast("Member added to group", "success");
      setAddMemberModalOpen(false);
    },
    onError: (error) => {
      console.error("Error adding member to group chat:", error);
      showToast("Failed to add member", "error");
    },
  });

  const handleCreateGroupChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupChatName.trim()) return;

    if (!user?.id) return;

    createGroupChatMutation.mutate({
      name: newGroupChatName.trim(),
      created_by: user.id,
    });
  };

  const handleJoinGroupChat = (groupChatId: string) => {
    navigate(`/group-chat/${groupChatId}`);
  };

  const handleOpenAddMemberModal = (
    groupChatId: string,
    name: string | null
  ) => {
    setTargetGroupChat({ id: groupChatId, name });
    setContactSearch("");
    setAddMemberModalOpen(true);
  };

  const handleSelectContact = (contact: ContactRow) => {
    if (!targetGroupChat) {
      showToast("Select a group chat first", "error");
      return;
    }

    if (!contact.related_user_id) {
      showToast("Contact is missing an associated user", "error");
      return;
    }

    addMemberMutation.mutate({
      groupChatId: targetGroupChat.id,
      userId: contact.related_user_id,
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <ThemedSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Typography variant="h3" className="text-red-600 mb-4">
          Error loading group chats
        </Typography>
        <Typography variant="body1" className="text-gray-600">
          Please try refreshing the page.
        </Typography>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Typography variant="h2" className="mb-8 text-terracotta">
          Group Chats
        </Typography>

        {/* Create New Group Chat Section */}
        <Card className="mb-8" variant="cream" elevation={2}>
          <Card.Header variant="cream">
            <Typography variant="h4" className="text-primary">
              Create New Group Chat
            </Typography>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handleCreateGroupChat} className="space-y-4">
              <TamaguiInput
                id="groupChatName"
                label="Group Chat Name"
                value={newGroupChatName}
                onChangeText={setNewGroupChatName}
                placeholder="Enter group chat name"
                fullWidth
                inputType="text"
              />
              <SubmitButton
                type="submit"
                isDisabled={
                  !newGroupChatName.trim() || createGroupChatMutation.isPending
                }
                isLoading={createGroupChatMutation.isPending}
                icon={<PlusIcon className="w-4 h-4" />}
              >
                Create
              </SubmitButton>
            </form>
          </Card.Body>
        </Card>

        {/* Group Chats List */}
        <div className="space-y-4">
          <Typography variant="h4" className="text-primary mb-4">
            Your Group Chats ({groupChats.length})
          </Typography>

          {groupChats.length === 0 ? (
            <Card variant="cream" elevation={1}>
              <Card.Body>
                <div className="text-center py-8">
                  <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <Typography variant="h5" className="text-gray-600 mb-2">
                    No group chats yet
                  </Typography>
                  <Typography variant="body2" className="text-gray-500">
                    Create your first group chat to start collaborating with
                    others.
                  </Typography>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <div className="grid gap-4">
              {groupChats.map((groupChat) => (
                <Card key={groupChat.id} variant="cream" elevation={1}>
                  <Card.Body>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-terracotta-100 rounded-full flex items-center justify-center">
                          <UsersIcon className="w-5 h-5 text-terracotta-600" />
                        </div>
                        <div>
                          <Typography variant="h5" className="text-primary">
                            {groupChat.name}
                          </Typography>
                          <Typography variant="body2" className="text-gray-600">
                            Created{" "}
                            {groupChat.created_at
                              ? new Date(
                                  groupChat.created_at
                                ).toLocaleDateString()
                              : "—"}
                          </Typography>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <SubmitButton
                          size="sm"
                          color="terracotta"
                          onClick={() => handleJoinGroupChat(groupChat.id)}
                          icon={<ChatBubbleLeftRightIcon className="w-4 h-4" />}
                        >
                          Chat
                        </SubmitButton>

                        <SubmitButton
                          size="sm"
                          color="beige"
                          onClick={() =>
                            handleOpenAddMemberModal(
                              groupChat.id,
                              groupChat.name
                            )
                          }
                          icon={<UserPlusIcon className="w-4 h-4" />}
                        >
                          Add Member
                        </SubmitButton>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isAddMemberModalOpen}
        onClose={() => setAddMemberModalOpen(false)}
        title={`Add member${
          targetGroupChat?.name ? ` to ${targetGroupChat.name}` : ""
        }`}
      >
        {contactsLoading ? (
          <div className="flex justify-center py-8">
            <ThemedSpinner />
          </div>
        ) : contacts.length === 0 ? (
          <Typography variant="body2" className="text-center text-brown-600">
            You don't have any contacts yet. Create a contact first to add them
            to a group chat.
          </Typography>
        ) : (
          <div className="space-y-4">
            <Input
              label="Search contacts"
              placeholder="Type a name, email, or phone"
              value={contactSearch}
              onChange={(event) => setContactSearch(event.target.value)}
              fullWidth
            />
            <div className="max-h-80 overflow-y-auto divide-y divide-cream-200">
              {filteredContacts.length === 0 ? (
                <Typography
                  variant="body2"
                  className="py-6 text-center text-brown-500"
                >
                  No contacts match that search.
                </Typography>
              ) : (
                filteredContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between py-3"
                  >
                    <div>
                      <Typography
                        variant="body1"
                        className="text-terracotta-700"
                      >
                        {contact.related_user.first_name || ""}{" "}
                        {contact.related_user.last_name || ""}
                      </Typography>
                      <Typography variant="caption" className="text-brown-500">
                        {contact.related_user.email ||
                          contact.related_user.phone ||
                          "No contact info"}
                      </Typography>
                    </div>
                    <SubmitButton
                      size="sm"
                      color="terracotta"
                      isLoading={addMemberMutation.isPending}
                      onClick={() => handleSelectContact(contact)}
                    >
                      Add
                    </SubmitButton>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GroupsChatPageList;
